import { useSSE } from "@sunner-cloud/api-client";
import { type ReactNode, useCallback, useMemo, useRef } from "react";

import type { PlantStatus } from "./PlantStatus";
import {
  PlantStatusContext,
  type PlantStatusStore,
} from "./PlantStatusContext";
import { mapPlantStatus } from "./plantStatusMapper";

import { baseUrl } from "@/clientConfig";
import { useAuth } from "@/providers/auth";

const buildAuthToken = (token: string | null) =>
  token ? `Bearer ${token}` : undefined;

interface Props {
  children: ReactNode;
}

/**
 * Una única suscripción SSE a `/plants/status` para toda la sesión. El mapa
 * persiste entre navegaciones (mapa caliente) y mantiene caliente el broadcaster
 * del backend, eliminando el cold-start por navegación y las conexiones
 * duplicadas (#3363/#3365). Los consumidores leen vía `useSyncExternalStore` con
 * selección fina por planta para no re-renderizar por eventos de otras plantas.
 */
export const PlantStatusProvider = ({ children }: Props) => {
  const { token, logout } = useAuth();

  const mapRef = useRef<Map<string, PlantStatus>>(new Map());
  const versionRef = useRef(0);
  // Snapshot clonado para el consumidor whole-map: su identidad debe cambiar con
  // cada evento para invalidar los useMemo aguas abajo (p. ej. usePlantListData),
  // sin clonar en cada render (solo cuando sube la versión).
  const snapshotRef = useRef<Map<string, PlantStatus>>(new Map());
  const snapshotVersionRef = useRef(-1);
  const byIdListenersRef = useRef(new Map<string, Set<() => void>>());
  const allListenersRef = useRef(new Set<() => void>());

  const handleMessage = useCallback((plantStatus: PlantStatus) => {
    mapRef.current.set(plantStatus.plantId, plantStatus);
    versionRef.current += 1;
    byIdListenersRef.current.get(plantStatus.plantId)?.forEach((listener) => {
      listener();
    });
    allListenersRef.current.forEach((listener) => {
      listener();
    });
  }, []);

  const { error, isLoading } = useSSE<"/plants/status", PlantStatus>(
    "/plants/status",
    {
      baseUrl,
      map: mapPlantStatus,
      onMessage: handleMessage,
      // Sin sesión no se abre el firehose (RootLayout también renderiza en /login).
      disabled: !token,
      authToken: buildAuthToken(token),
      onAuthError: logout,
    },
  );

  // Funciones estables (cierran solo sobre refs): su identidad no cambia con
  // error/isLoading, así los consumidores no re-suscriben en cada reconexión.
  const fns = useMemo(() => {
    const subscribeById = (plantId: string, listener: () => void) => {
      let listeners = byIdListenersRef.current.get(plantId);
      if (!listeners) {
        listeners = new Set();
        byIdListenersRef.current.set(plantId, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          byIdListenersRef.current.delete(plantId);
        }
      };
    };

    const subscribeAll = (listener: () => void) => {
      allListenersRef.current.add(listener);
      return () => {
        allListenersRef.current.delete(listener);
      };
    };

    const getById = (plantId: string) => mapRef.current.get(plantId) ?? null;

    const getMapSnapshot = () => {
      if (snapshotVersionRef.current !== versionRef.current) {
        snapshotRef.current = new Map(mapRef.current);
        snapshotVersionRef.current = versionRef.current;
      }
      return snapshotRef.current;
    };

    return { subscribeById, subscribeAll, getById, getMapSnapshot };
  }, []);

  const store = useMemo<PlantStatusStore>(
    () => ({ ...fns, error: error ?? false, isLoading }),
    [fns, error, isLoading],
  );

  return <PlantStatusContext value={store}>{children}</PlantStatusContext>;
};
